import { DomainUpsert } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { Input } from "@/components/ui/input";
import { RatingInput } from "@/components/ui/rate-input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useGetProviders } from "@/hooks/providers";
import { useGetRegions } from "@/hooks/regions";
import { useGetServices } from "@/hooks/services";
import {
  CreateServiceProvider,
  PaymentModelList,
  ServiceProviderDomain,
} from "@/models";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function Form({
  model,
  onUpsert,
  domain,
  domainId,
  name,
}: {
  domainId: string;
  domain: ServiceProviderDomain;
  model?: CreateServiceProvider;
  onUpsert: (data: CreateServiceProvider) => Promise<void>;
  name?: string;
}) {
  const { data: regions, isLoading: regionsLoading } = useGetRegions();
  const { data: services, isLoading: servicesLoading } = useGetServices({
    enabled: !model && domain === "provider",
  });
  const { data: providers, isLoading: providersLoading } = useGetProviders({
    enabled: !model && domain === "service",
  });
  const { t } = useTranslation();
  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreateServiceProvider>>(() => {
    const config: FormOptions<CreateServiceProvider> = {
      serviceId: {
        value: domain === "service" ? domainId : model?.serviceId || "",
        validator: z.string().min(1, "errors.required"),
      },
      providerId: {
        value: domain === "provider" ? domainId : model?.providerId || "",
        validator: z.string().min(1, "errors.required"),
      },
      rating: {
        value: Math.abs(model?.rating ?? 0) || 5,
      },
      requiresPrice: {
        value: model?.requiresPrice || false,
      },
      price: {
        value: model?.price,
      },
      regionId: {
        value: model?.regionId,
      },
      paymentModel: { value: model?.paymentModel },
    };
    return config;
  }, [model, domain, domainId]);

  return (
    <DomainUpsert
      title={name ?? t(`serviceProviders.${domain}.new.title`)}
      buttonTranslation={
        isEdit
          ? `serviceProviders.${domain}.update.button`
          : `serviceProviders.${domain}.new.button`
      }
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        !model && domain === "service"
          ? {
              key: "providerId",
              label: "serviceProviders.fields.providerId",
              input: (form, id) =>
                providersLoading || !providers ? (
                  <Skeleton className="h-9 w-full" />
                ) : (
                  <SelectSearch
                    id={id}
                    className="w-full"
                    value={form.value.providerId ?? undefined}
                    onChange={(v) => {
                      form.setValue("providerId", v);
                    }}
                    options={
                      providers?.map((v) => ({
                        value: v.id,
                        label: v.name,
                      })) ?? []
                    }
                  />
                ),
            }
          : null,
        !model && domain === "provider"
          ? {
              key: "serviceId",
              label: "serviceProviders.fields.serviceId",
              input: (form, id) =>
                servicesLoading || !services ? (
                  <Skeleton className="h-9 w-full" />
                ) : (
                  <SelectSearch
                    id={id}
                    className="w-full"
                    value={
                      typeof form.value.serviceId === "string"
                        ? Number.parseInt(form.value.serviceId)
                        : undefined
                    }
                    onChange={(v) => {
                      form.setValue("serviceId", v.toString());
                      const service = services.find((s) => s.id === v);
                      form.setValue("paymentModel", service?.paymentModel);
                      form.setValue("price", service?.referencePrice);
                    }}
                    options={
                      services?.map((v) => ({
                        value: v.id,
                        label: v.name,
                      })) ?? []
                    }
                  />
                ),
            }
          : null,
        {
          key: "requiresPrice",
          label: "serviceProviders.fields.requiresPrice",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("requiresPrice", v);
              }}
              checked={!!form.value.requiresPrice}
            />
          ),
        },
        {
          key: "price",
          label: "serviceProviders.fields.price",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.price ?? undefined}
              inputMode="decimal"
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue("price", Number.parseFloat(e.target.value));
              }}
            />
          ),
        },
        {
          key: "paymentModel",
          label: "serviceProviders.fields.paymentModel",
          input: (form, id) => (
            <SelectSearch
              id={id}
              value={form.value.paymentModel ?? undefined}
              onChange={(v) => {
                form.setValue("paymentModel", v);
              }}
              options={PaymentModelList.map((v) => ({
                value: v,
                label: t(`services.paymentModels.${v}`),
              }))}
            />
          ),
        },

        {
          key: "rating",
          label: "serviceProviders.fields.rating",
          input: (form, id) => (
            <RatingInput
              id={id}
              value={form.value.rating}
              onChange={(v) => {
                form.setValue("rating", v);
              }}
            />
          ),
        },
        {
          key: "regionId",
          label: "serviceProviders.fields.regionId",
          input: (form, id) =>
            regionsLoading || !regions ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                className="w-full"
                value={form.value.regionId ?? undefined}
                onChange={(v) => {
                  form.setValue("regionId", v);
                }}
                options={regions.map((v) => ({
                  value: v.id,
                  label: v.name,
                }))}
              />
            ),
        },
      ]}
    />
  );
}
